<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Project List'" />
    <div class="mt-4 w-full flex flex-col m-auto" style="height: 90vh">
      <Toolbar pt:root:class="mx-4 py-2">
        <template #start>
          <div class="flex flex-row gap-2">
            <SearchBar v-model:search-text="searchText" :show-refresh="true" @input="onFilterTextBoxChanged" @refresh="getProjects"></SearchBar>
            <ToolbarButtons
              label="Projects"
              show-import
              show-delete
              show-archive
              show-unlock
              :import-disabled="userDetail?.drawAssistant || gridApi == null"
              :archive-disabled="userDetail?.drawAssistant || selectedProjects.length === 0 || gridApi == null"
              :delete-disabled="
                (!userDetail?.administrator && !userDetail?.permissions.deleteProject) || selectedProjects.length == 0 || gridApi == null
              "
              :unlock-disabled="!userDetail?.administrator"
              @import="openImportModal"
              @archive="tryArchiveProjects"
              @delete="tryDeleteProjects"
              @unlock="tryUnlockAllProjects"
            ></ToolbarButtons>
          </div>
        </template>
        <template #center>
          <div class="flex flex-row gap-3">
            <div v-for="filter in filters" :key="filter.name" class="flex items-center">
              <RadioButton v-model="selectedFilter" :inputId="filter.name" name="dynamic" :value="filter.name" @change="gridApi?.onFilterChanged()" />
              <label :for="filter.name" class="ml-1 text-sm dark:text-surface-0/80">{{ filter.name }} ({{ filter.amount }})</label>
            </div>
          </div>
        </template>
        <template #end>
          <div class="flex flex-row gap-2">
            <Button icon="pi pi-inbox" label="Archive By Date" class="h-10" :disabled="userDetail?.drawAssistant" @click="showArchiveByDate"></Button>
            <ReportsListButton :context="'ALL_PROJECTS'"></ReportsListButton>
          </div>
        </template>
      </Toolbar>
      <div class="h-[90vh]">
        <ag-grid-vue
          v-if="selectedFilter !== 'Archived'"
          theme="legacy"
          :rowData="projects"
          :get-row-id="getRowId"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          :getRowStyle="getRowStyle"
          :loading="projects == null"
          :enableFilter="true"
          pagination
          :paginationPageSize="50"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          rowSelection="multiple"
          :isExternalFilterPresent="isExternalFilterPresent"
          :doesExternalFilterPass="doesExternalFilterPass"
          @grid-size-changed="resize"
          @row-double-clicked="projectSelected"
          @selection-changed="projectSelectionChanged"
          @grid-ready="
            (event: GridReadyEvent) => {
              gridApi = event.api;
              applyPreFilter(event);
            }
          "
        >
        </ag-grid-vue>
        <ag-grid-vue
          v-if="selectedFilter === 'Archived'"
          theme="legacy"
          :rowData="archived"
          :columnDefs="archivedColDefs"
          :defaultColDef="archivedDefaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          :loading="projects == null"
          pagination
          :paginationPageSize="50"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          rowSelection="multiple"
          :get-row-id="getRowId"
          @grid-size-changed="resize"
          @grid-ready="(event: GridReadyEvent) => (archivedGridApi = event.api)"
        >
        </ag-grid-vue>
      </div>
    </div>
    <TSDialog v-model:visible="displayImportModal" label="Import Project" icon="file-import" size="w-[1000px]!">
      <template #content>
        <ProjectFileImport v-if="userDetail" :userDetail></ProjectFileImport>
      </template>
    </TSDialog>
    <TSDialog v-model:visible="displayArchiveByDate" label="Archive Projects" icon="inbox" size="w-[500px]!">
      <template #content>
        <div class="w-full flex flex-col items-center gap-4">
          <p class="text-center">Select Date for Archiving - All sold projects with last activity before this date with be archived</p>

          <div class="w-full flex justify-center gap-2">
            <DatePicker
              v-model="archiveDate"
              show-button-bar
              showIcon
              :show-on-focus="false"
              :manualInput="true"
              icon-display="button"
              inputId="archiveDate"
              class="h-10"
              input-class="w-60!"
              panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
              dateFormat="m/d/yy"
            />
            <SubmitButton :disabled="archiveDate == null" label="Archive" :show-icon="false" @click="archiveByDate"></SubmitButton>
          </div>
        </div>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ProjectSummaryAdminListSchema, type ProjectSummary } from "@/types/Projects";
import { usePropsStore } from "@/stores/props";
import { currencyFormatter, dateFormatter, getTableData, parseData } from "@/services/Utility";
import { type Setting, SettingListSchema } from "@/types/Settings";
import { ColorSettings } from "@/enums/ColorSettings";
import { DrawStatus } from "@/enums/DrawStatus";
import { ProjectStatusCategory } from "@/enums/ProjectStatusCategory";
import { getUserDetail } from "@/services/UserService";
import type { UserDetail } from "@/types/User";
import { useConfirm } from "primevue/useconfirm";
import { ArchivedProjectListSchema, type ArchivedProject } from "@/types/ArchivedProject";
import type {
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowDoubleClickedEvent,
  RowStyle,
  ValueFormatterParams,
} from "ag-grid-community";
import { useAuthStore } from "@/stores/auth";

const confirm = useConfirm();

const gridApi = ref<GridApi>();
const archivedGridApi = ref<GridApi>();

const router = useRouter();

const projects = ref<ProjectSummary[]>();
const archived = ref<ArchivedProject[]>([]);
const colorSettings = ref<Setting[]>([]);
const displayImportModal = ref(false);
const displayArchiveByDate = ref(false);
const archiveDate = ref<Date>();

const userDetail = ref<UserDetail>();

const selectedProjects = ref<any[]>([]);
const selectedArchived = ref<any[]>([]);

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const searchText = ref(propsStore.searchText);

const selectedFilter = ref<"Active/Pending" | "Completed" | "Approved" | "Sold" | "All" | "Archived">("Active/Pending");
const filters = ref([
  { name: "Active/Pending", amount: 0 },
  { name: "Completed", amount: 0 },
  { name: "Approved", amount: 0 },
  { name: "Sold", amount: 0 },
  { name: "All", amount: 0 },
  { name: "Archived", amount: 0 },
]);

const projectSelected = (event: RowDoubleClickedEvent): void => {
  const authStore = useAuthStore();
  const userInfo = authStore.getUserInfo();
  if (event.data.locked && event.data.lockedBy !== userInfo.username) {
    error("Project Locked", "Currently locked by " + event.data.lockedBy);
  } else {
    router.push({
      name: "project-dashboard",
      params: {
        id: event.data.projectId,
      },
    });
  }
};

const getRowId = (params: GetRowIdParams): string => {
  return params.data.projectId;
};

const projectSelectionChanged = (): void => {
  if (gridApi.value) selectedProjects.value = gridApi.value.getSelectedNodes();
  if (archivedGridApi.value) selectedArchived.value = archivedGridApi.value.getSelectedNodes();
};

const applyPreFilter = (event: GridReadyEvent): void => {
  const authStore = useAuthStore();
  const userInfo = authStore.getUserInfo();
  let display = userInfo.firstName;
  if (userInfo.lastName.length !== 0) {
    display += " " + userInfo.lastName;
  }
  const filter = {
    filterType: "text",
    type: "equals",
    filter: display,
  };
  event.api.setColumnFilterModel("processor", filter);
  event.api.onFilterChanged();
};

const isExternalFilterPresent = () => {
  return selectedFilter.value !== "All";
};

const doesExternalFilterPass = (node: any): boolean => {
  const project: ProjectSummary = node.data;

  switch (selectedFilter.value) {
    case "Active/Pending":
      return project.projectStatus.category === ProjectStatusCategory.ACTIVE || project.projectStatus.category === ProjectStatusCategory.PENDING;
    case "Completed":
      return project.drawStatus === DrawStatus.COMPLETED;
    case "Approved":
      return project.drawStatus === DrawStatus.APPROVED;
    case "Sold":
      return project.projectStatus.category === ProjectStatusCategory.SOLD;
    default:
      return true;
  }
};

const calculateTotals = (): void => {
  let activePending = 0;
  let completed = 0;
  let approved = 0;
  let sold = 0;
  projects.value?.forEach((project: ProjectSummary) => {
    if (project.projectStatus.category === ProjectStatusCategory.ACTIVE || project.projectStatus.category === ProjectStatusCategory.PENDING) {
      activePending++;
    } else if (project.projectStatus.category === ProjectStatusCategory.SOLD) {
      sold++;
    }

    if (project.drawStatus === DrawStatus.COMPLETED) {
      completed++;
    } else if (project.drawStatus === DrawStatus.APPROVED) {
      approved++;
    }
  });

  filters.value.forEach((value) => {
    if (value.name === "Active/Pending") value.amount = activePending;
    else if (value.name === "Completed") value.amount = completed;
    else if (value.name === "Approved") value.amount = approved;
    else if (value.name === "Sold") value.amount = sold;
    else if (value.name === "All") value.amount = projects.value != null ? projects.value.length : 0;
    else if (value.name === "Archived") value.amount = archived.value != null ? archived.value.length : 0;
  });
};

const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
  if (params.data.locked) {
    return getColorForSetting(ColorSettings.PROJECT_LOCKED_COLOR);
  }

  if (params.data.drawStatus === DrawStatus.APPROVED) {
    return getColorForSetting(ColorSettings.DRAW_APPROVED_COLOR);
  }

  if (params.data.drawStatus === DrawStatus.DECLINED) {
    return getColorForSetting(ColorSettings.PROJECT_DECLINED_COLOR);
  }

  if (params.data.hasNewDraw) {
    return getColorForSetting(ColorSettings.NEW_DRAW_AVAILABLE_COLOR);
  }

  if (params.data.hasImportedDraws) {
    return getColorForSetting(ColorSettings.NEW_DRAW_REQUEST_COLOR);
  }

  if (params.data.hasImportedInspections) {
    return getColorForSetting(ColorSettings.NEW_INSPECTION_REQUEST_COLOR);
  }

  if (params.data.hasNewPortalFiles) {
    return getColorForSetting(ColorSettings.NEW_FILES_AVAILABLE_COLOR);
  }

  if (params.data.projectCode === "TBD") {
    return getColorForSetting(ColorSettings.TBD_PROJECT_COLOR);
  }

  if (params.data.imported) {
    return getColorForSetting(ColorSettings.PROJECT_IMPORTED_COLOR);
  }

  return {};
};

const getColorForSetting = (setting: ColorSettings): RowStyle | undefined => {
  for (let color of colorSettings.value) {
    if (color.settingName === setting) {
      let colorValue = color.settingValue;

      if (!colorValue) return undefined;

      if (!colorValue.startsWith("#")) {
        colorValue = "#" + colorValue;
      }

      const rgb = hexToRGB(colorValue);
      if (rgb != null) {
        const brightness = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
        if (brightness < 127) {
          return {
            background: colorValue,
            color: "white",
          };
        } else {
          return {
            background: colorValue,
            color: "black",
          };
        }
      }

      return { background: colorValue };
    }
  }
  return undefined;
};

const hexToRGB = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const onFilterTextBoxChanged = (): void => {
  if (gridApi.value) gridApi.value.setGridOption("quickFilterText", searchText.value);
  if (archivedGridApi.value) archivedGridApi.value.setGridOption("quickFilterText", searchText.value);
  propsStore.searchText = searchText.value;
};

const resize = (): void => {
  if (gridApi.value) gridApi.value?.sizeColumnsToFit();
  if (archivedGridApi.value) archivedGridApi.value.sizeColumnsToFit();
};

const defaultColDefs = {
  filter: true,
  floatingFilter: true,
  cellDataType: "text",
  autoHeaderHeight: true,
  menuTabs: [],
};

const colDefs = ref([
  {
    field: "userGroup",
    headerName: "User Group",
    minWidth: 125,
    width: 125,
    filter: "agTextColumnFilter",
    headerCheckboxSelection: true,
    checkboxSelection: true,
  },
  {
    field: "currentDraw",
    headerName: "#",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    minWidth: 50,
    width: 75,
    filter: false,
  },
  {
    field: "projectCode",
    headerName: "Project ID",
    minWidth: 100,
    filter: false,
  },
  {
    field: "projectName",
    headerName: "Project Name",
    minWidth: 225,
    filter: false,
  },
  {
    field: "processor",
    headerName: "Processor",
    minWidth: 150,
    filter: "agTextColumnFilter",
  },
  {
    field: "borrower",
    headerName: "Borrower",
    minWidth: 150,
    filter: "agTextColumnFilter",
  },
  {
    field: "builder",
    headerName: "Builder",
    minWidth: 150,
    filter: "agTextColumnFilter",
  },
  {
    field: "inspector",
    headerName: "Inspector",
    minWidth: 150,
    filter: "agTextColumnFilter",
  },
  {
    field: "budget",
    headerName: "Budget Amount",
    valueFormatter: currencyFormatter,
    minWidth: 125,
    filter: false,
  },
  {
    field: "drawnToDate",
    headerName: "Drawn To Date",
    valueFormatter: currencyFormatter,
    minWidth: 125,
    filter: false,
  },
  {
    field: "fundsAvailable",
    headerName: "Funds Available",
    valueFormatter: currencyFormatter,
    minWidth: 125,
    filter: false,
  },
  {
    field: "lastUpdatedBy",
    headerName: "Last Updated By",
    minWidth: 150,
    filter: "agTextColumnFilter",
  },
]);

const archivedDefaultColDefs = {
  filter: true,
  cellDataType: "text",
  autoHeaderHeight: true,
  menuTabs: [],
};

const archivedColDefs = ref([
  {
    field: "projectCode",
    headerName: "Project ID",
    minWidth: 100,
    filter: false,
    headerCheckboxSelection: true,
    checkboxSelection: true,
  },
  {
    field: "projectName",
    headerName: "Project Name",
    minWidth: 200,
    filter: false,
  },
  {
    field: "archiveDate",
    headerName: "Archive Date",
    cellDataType: "date",
    valueFormatter: (params: ValueFormatterParams) => dateFormatter(params),
    minWidth: 100,
    width: 100,
  },
  {
    field: "lastDrawCreatedDate",
    headerName: "Last Draw Created",
    cellDataType: "date",
    valueFormatter: (params: ValueFormatterParams) => dateFormatter(params),
    minWidth: 100,
    width: 100,
  },
]);

const openImportModal = (): void => {
  displayImportModal.value = true;
};

const showArchiveByDate = (): void => {
  displayArchiveByDate.value = true;
  archiveDate.value = undefined;
};

const archiveByDate = (): void => {
  if (projects.value == null) return;
  const projectsToArchive = projects.value
    .filter(
      (project) =>
        project.projectStatus.category === ProjectStatusCategory.SOLD &&
        archiveDate.value != null &&
        project.lastActivity != null &&
        project.lastActivity < archiveDate.value
    )
    .map((project) => project.projectId);

  if (projectsToArchive.length === 0) error("No projects to Archive before that date", "");
  else archiveProjects(projectsToArchive);
};

const tryArchiveProjects = (): void => {
  const confirmMessage =
    selectedProjects.value.length === 1
      ? `Are you sure you want to archive project ${selectedProjects.value[0].data.projectName}?`
      : `Are you sure you want to archive ${selectedProjects.value.length} projects?`;
  confirmPopup(
    confirm,
    "Archive Projects?",
    confirmMessage,
    () => archiveProjects(selectedProjects.value.map((selectedProject) => selectedProject.data.projectId)),
    () => {
      info("Archive Cancelled", "");
    }
  );
};

const archiveProjects = (projectsToArchive: string[]): void => {
  axios
    .post("/projects/archive", projectsToArchive)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success(projectsToArchive.length + " Project(s) archived", "");
          const projectsArchived = projectsToArchive.map((id) => ({ projectId: id }));
          gridApi.value?.applyTransaction({
            remove: projectsArchived,
          });
          projects.value = getTableData(gridApi.value);
          selectedProjects.value = [];
          const newRows: ArchivedProject[] = ArchivedProjectListSchema.parse(data);
          archived.value = archived.value.concat(newRows);
          calculateTotals();
        },
        (errors: ApiError[]) => {
          error("Error Archiving Project(s)", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const tryDeleteProjects = (): void => {
  const confirmMessage =
    selectedProjects.value.length === 1
      ? `Are you sure you want to delete project ${selectedProjects.value[0].data.projectName}?`
      : `Are you sure you want to delete ${selectedProjects.value.length} projects?`;
  confirmPopup(confirm, "Delete Projects?", confirmMessage, deleteProjects, () => {
    info("Delete Cancelled", "");
  });
};

const deleteProjects = (): void => {
  const projectsToDelete: string[] = selectedProjects.value.map((selectedProject) => selectedProject.data.projectId);
  axios
    .post("/projects/delete", projectsToDelete)
    .then((response) => {
      handleApiResponse(
        response,
        () => {
          success("Project(s) deleted", "");
          const projectsRemoved = selectedProjects.value.map((selectedProjects) => ({ projectId: selectedProjects.data.projectId }));
          gridApi.value?.applyTransaction({
            remove: projectsRemoved,
          });
          projects.value = getTableData(gridApi.value);
          selectedProjects.value = [];
          calculateTotals();
        },
        (errors: ApiError[]) => {
          error("Error Deleting Project(s)", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const tryUnlockAllProjects = (): void => {
  confirmPopup(confirm, "Unlock Projects?", "Are you sure you want to unlock all projects", unlockAllProjects, () => {
    info("Unlock Cancelled", "");
  });
};

const unlockAllProjects = (): void => {
  axios.post("/projects/unlock-all").then((response) => {
    handleApiResponse(
      response,
      () => {
        getProjects();
      },
      (errors: ApiError[]) => {
        error("Error occurred while deleting file", errors[0].message);
      }
    );
  });
};

const getProjects = async (): Promise<void> => {
  await axios
    .get("/projects")
    .then((response) => {
      const data = ProjectSummaryAdminListSchema.parse(response.data.result);
      projects.value = data.projects;
      archived.value = data.archived;
    })
    .catch((e) => errorApi(e));

  calculateTotals();

  await nextTick();
  gridApi.value?.redrawRows();
};

onBeforeMount(async (): Promise<void> => {
  await axios
    .get("/settings/system?type=COLOR")
    .then((response) => {
      parseData(SettingListSchema, response.data.result, colorSettings);
    })
    .catch((e) => errorApi(e));

  await getUserDetail().then((result: UserDetail | null) => {
    if (result == null) return;

    userDetail.value = result;
  });

  await getProjects();
  onFilterTextBoxChanged();
});
</script>
